import { useState, useRef } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { PlusCircle, Upload, Download } from "lucide-react";

const emptyForm = () => ({
    asset_tag: "",
    kind: "cart",
    size: "96gal",
    condition: "good",
    status: "available",
    notes: "",
});

export function AddInventoryDialog({ open, onOpenChange, onCreated }) {
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState(emptyForm());
    const set = (k, v) => setForm((c) => ({ ...c, [k]: v }));

    const submit = async () => {
        setSaving(true);
        try {
            const body = { ...form };
            if (!body.asset_tag.trim()) delete body.asset_tag; // let server auto-gen
            await api.post("/inventory", body);
            toast.success("Cart added to inventory");
            setForm(emptyForm());
            onOpenChange(false);
            onCreated?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to add cart");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent className="w-full sm:max-w-md"
                          data-testid="add-inventory-dialog">
                <SheetHeader>
                    <SheetTitle className="flex items-center gap-2">
                        <PlusCircle className="w-4 h-4" />Add cart / container
                    </SheetTitle>
                    <SheetDescription>
                        Track a new physical asset. Asset tag auto-generates if left blank.
                    </SheetDescription>
                </SheetHeader>
                <div className="mt-6 space-y-3">
                    <F label="Asset tag (optional)">
                        <Input value={form.asset_tag} placeholder="AST-XXXXX (auto)"
                               onChange={(e) => set("asset_tag", e.target.value)}
                               data-testid="inv-asset-tag" />
                    </F>
                    <div className="grid grid-cols-2 gap-3">
                        <F label="Kind">
                            <Select value={form.kind}
                                    onValueChange={(v) => set("kind", v)}>
                                <SelectTrigger data-testid="inv-kind"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="cart">Cart</SelectItem>
                                    <SelectItem value="rolloff_container">Roll-off container</SelectItem>
                                    <SelectItem value="lid">Lid</SelectItem>
                                    <SelectItem value="wheel">Wheel</SelectItem>
                                    <SelectItem value="part">Part</SelectItem>
                                </SelectContent>
                            </Select>
                        </F>
                        <F label="Size">
                            <Input value={form.size}
                                   onChange={(e) => set("size", e.target.value)}
                                   placeholder="96gal / 20yd" />
                        </F>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <F label="Condition">
                            <Select value={form.condition}
                                    onValueChange={(v) => set("condition", v)}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="new">New</SelectItem>
                                    <SelectItem value="good">Good</SelectItem>
                                    <SelectItem value="fair">Fair</SelectItem>
                                    <SelectItem value="damaged">Damaged</SelectItem>
                                </SelectContent>
                            </Select>
                        </F>
                        <F label="Status">
                            <Select value={form.status}
                                    onValueChange={(v) => set("status", v)}>
                                <SelectTrigger><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="available">Available</SelectItem>
                                    <SelectItem value="deployed">Deployed</SelectItem>
                                    <SelectItem value="in_repair">In repair</SelectItem>
                                    <SelectItem value="lost">Lost</SelectItem>
                                </SelectContent>
                            </Select>
                        </F>
                    </div>
                    <F label="Notes">
                        <Textarea rows={2} value={form.notes}
                                  onChange={(e) => set("notes", e.target.value)} />
                    </F>
                    <div className="flex justify-end gap-2 pt-4 border-t border-border">
                        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                        <Button onClick={submit} disabled={saving}
                                data-testid="inv-add-submit">
                            {saving ? "Adding..." : "Add asset"}
                        </Button>
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
}

export function ImportInventoryDialog({ open, onOpenChange, onImported }) {
    const fileRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const downloadTemplate = async () => {
        try {
            const { data } = await api.get("/inventory/import/template.csv",
                                             { responseType: "blob" });
            const url = URL.createObjectURL(data);
            const a = document.createElement("a");
            a.href = url;
            a.download = "inventory_template.csv";
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error("Template download failed");
        }
    };

    const submit = async () => {
        const file = fileRef.current?.files?.[0];
        if (!file) {
            toast.error("Pick a CSV first");
            return;
        }
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/inventory/import", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setResult(data);
            toast.success(`Imported ${data.inserted}, skipped ${data.skipped}`);
            onImported?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Import failed");
        } finally {
            setBusy(false);
        }
    };

    const reset = () => {
        setResult(null);
        if (fileRef.current) fileRef.current.value = "";
    };

    return (
        <Sheet open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
            <SheetContent className="w-full sm:max-w-lg"
                          data-testid="import-inventory-dialog">
                <SheetHeader>
                    <SheetTitle className="flex items-center gap-2">
                        <Upload className="w-4 h-4" />Import carts / containers CSV
                    </SheetTitle>
                    <SheetDescription>
                        Columns: <code className="text-xs">asset_tag,kind,size,condition,status,notes</code>.
                        Duplicate asset_tags are skipped.
                    </SheetDescription>
                </SheetHeader>

                {!result && (
                    <div className="mt-6 space-y-3">
                        <button onClick={downloadTemplate}
                                className="text-xs text-primary hover:underline flex items-center gap-1">
                            <Download className="w-3 h-3" />Download template
                        </button>
                        <input ref={fileRef} type="file" accept=".csv"
                               className="block w-full text-sm border border-input rounded-md p-2 bg-background"
                               data-testid="import-inv-file" />
                        <div className="flex justify-end gap-2 pt-4 border-t border-border">
                            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                            <Button onClick={submit} disabled={busy}
                                    data-testid="import-inv-submit">
                                {busy ? "Importing..." : "Import"}
                            </Button>
                        </div>
                    </div>
                )}

                {result && (
                    <div className="mt-6 space-y-3">
                        <div className="p-4 rounded-md border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-900">
                            <div className="text-sm font-semibold text-emerald-800 dark:text-emerald-200">
                                Imported {result.inserted} asset{result.inserted === 1 ? "" : "s"}
                            </div>
                            {result.skipped > 0 && (
                                <div className="text-xs text-muted-foreground mt-1">
                                    Skipped {result.skipped}
                                    {result.skipped_details?.length ? " — check details below." : "."}
                                </div>
                            )}
                        </div>
                        {result.skipped_details?.length > 0 && (
                            <div className="text-xs max-h-48 overflow-y-auto border rounded p-2 space-y-1">
                                {result.skipped_details.map((s, i) => (
                                    <div key={i} className="flex gap-2">
                                        <span className="font-mono">{s.asset_tag || "—"}</span>
                                        <span className="text-muted-foreground">{s.reason}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className="flex justify-end gap-2 pt-4 border-t border-border">
                            <Button variant="outline" onClick={reset}>Import another</Button>
                            <Button onClick={() => onOpenChange(false)}>Done</Button>
                        </div>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
}

function F({ label, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">{label}</Label>
            {children}
        </div>
    );
}
